import { api } from "../base";

const roleEndpoints = api.injectEndpoints({
  endpoints: (build) => ({
    /** Fetches roles for a user. */
    getUserRoles: build.query<string[], string>({
      query: (orgUserId) => ({ url: `/api/role/v1/${orgUserId}` }),
      providesTags: (_result, _error, orgUserId) => [
        { type: "UserRoles", id: orgUserId },
      ],
    }),

    /** Updates roles for a user. */
    updateUserRoles: build.mutation<
      void,
      { orgUserId: string; roles: string[] }
    >({
      query: ({ orgUserId, roles }) => ({
        url: `/api/role/v1/${orgUserId}`,
        method: "PUT",
        body: roles,
      }),
      invalidatesTags: (_result, _error, { orgUserId }) => [
        { type: "UserRoles", id: orgUserId },
      ],
    }),

    /** Fetches portfolio authorizations for a user. */
    getUserPortfoliosAuthz: build.query<string[], string>({
      query: (orgUserId) => ({
        url: `/api/authz/v1/${orgUserId}/portfolios`,
      }),
      transformResponse: (response: string[] | { data?: string[] }) =>
        Array.isArray(response) ? response : (response.data ?? []),
    }),

    /** Updates portfolio authorizations for a user. */
    updateUserPortfoliosAuthz: build.mutation<
      void,
      { orgUserId: string; portfolios: string[] }
    >({
      query: ({ orgUserId, portfolios }) => ({
        url: `/api/authz/v1/${orgUserId}/portfolios`,
        method: "PUT",
        body: { portfolios },
      }),
    }),

    /** Adds a new user to an org. */
    addUser: build.mutation<
      unknown,
      {
        orgId: string;
        firstname: string;
        lastname: string;
        email: string;
      }
    >({
      query: ({ orgId, ...body }) => ({
        url: `/api/account/v1/orgs/${orgId}/users`,
        method: "POST",
        body,
      }),
      invalidatesTags: ["OrgUsers"],
    }),

    /** Deletes a user from an org. */
    deleteUser: build.mutation<
      void,
      { orgId: string; orgUserId: string }
    >({
      query: ({ orgId, orgUserId }) => ({
        url: `/api/account/v1/orgs/${orgId}/users/${orgUserId}`,
        method: "DELETE",
      }),
      invalidatesTags: ["OrgUsers"],
    }),

    /** Fetches the login email for a user. */
    getUserLogin: build.query<string, string>({
      query: (orgUserId) => ({
        url: `/api/account/v1/user_login/${orgUserId}`,
      }),
      transformResponse: (response: { email?: string } | string) =>
        typeof response === "string" ? response : (response?.email ?? ""),
    }),
  }),
});

export const {
  useGetUserRolesQuery,
  useLazyGetUserRolesQuery,
  useUpdateUserRolesMutation,
  useGetUserPortfoliosAuthzQuery,
  useLazyGetUserPortfoliosAuthzQuery,
  useUpdateUserPortfoliosAuthzMutation,
  useAddUserMutation,
  useDeleteUserMutation,
  useGetUserLoginQuery,
  useLazyGetUserLoginQuery,
} = roleEndpoints;
